
export const HotAirBalloonIcon = ({ size = 24, className = "" }: { size?: number; className?: string }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <path d="M12 2a7 7 0 0 0-7 7c0 2.3 1.2 4.3 3 5.5v3a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-3c1.8-1.2 3-3.2 3-5.5a7 7 0 0 0-7-7z" />
    <path d="M9 16.5v1.5a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1v-1.5" />
    <path d="M12 19v2.5" />
    <path d="M10 22h4" />
    <path d="M5 9h14M12 2v14.5" />
  </svg>
);

/** `outerRing={false}` drops the dashed surround — for the mark sitting inside
 *  something already round, like the empty states' circular icon token. */
export const TalismanMark = ({ size = 40, className = "", outerRing = true }: { size?: number; className?: string; outerRing?: boolean }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    {outerRing && <circle cx="20" cy="20" r="19" stroke="#C5A059" strokeWidth="1" strokeDasharray="2 2" />}
    <path d="M20 8V32M8 20H32" stroke="#C5A059" strokeWidth="0.5" strokeOpacity="0.5" />
    <circle cx="20" cy="20" r="8" stroke="#C5A059" strokeWidth="1.5" />
    <circle cx="20" cy="20" r="3" fill="#C5A059" />
  </svg>
);

/** Standard lockup; welcome/loading and sign-in deliberately use stacked branding. */
export const TalismanLogo = () => (
  <span className="inline-flex items-center gap-2 whitespace-nowrap">
    <span aria-hidden="true" className="inline-flex shrink-0"><TalismanMark size={40} /></span>
    <span className="font-serif text-3xl font-black leading-none text-app-text">Talisman</span>
  </span>
);

export const VintageMapIcon = ({ size = 40, className = "" }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M10 8 L20 12 L30 8 L30 32 L20 28 L10 32 Z" />
    <path d="M20 12 V28M10 8 V32M30 8 V32" strokeWidth="1.5" opacity="0.6" />
    <path d="M16 16 C17 18 19 18 20 20 C21 21 21.5 22 23 22" strokeDasharray="1 3" strokeWidth="1.5" />
    <circle cx="15" cy="15" r="1.5" fill="currentColor" />
    <circle cx="24" cy="22" r="1.5" fill="currentColor" />
    <path d="M24 22 L24 18 L26 18 Z" fill="currentColor" strokeWidth="0" />
  </svg>
);

export const VintageCompassIcon = ({ size = 40, className = "" }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="20" cy="20" r="15" />
    <circle cx="20" cy="20" r="12" strokeWidth="1" opacity="0.5" strokeDasharray="2 2" />
    <circle cx="20" cy="20" r="2" fill="currentColor" />
    <path d="M20 8 L24 20 L20 32 L16 20 Z" />
    <path d="M20 8 L20 32" strokeWidth="1" opacity="0.5" />
    <path d="M20 5 V3M35 20 H37M5 20 H3M20 37 V35" strokeWidth="1" opacity="0.5" />
    <path d="M10 10 L8 8M30 30 L32 32M10 30 L8 32M30 10 L32 8" strokeWidth="1" opacity="0.5" />
  </svg>
);

export const VintageCoinIcon = ({ size = 40, className = "" }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <ellipse cx="20" cy="26" rx="14" ry="4" opacity="0.3" />
    <ellipse cx="20" cy="22" rx="14" ry="4" opacity="0.6" />
    <circle cx="20" cy="14" r="14" />
    <circle cx="20" cy="14" r="11" strokeWidth="1" strokeDasharray="1.5 2.5" opacity="0.8" />
    <path d="M16 14 L20 10 L24 14 L20 18 Z" opacity="0.8" />
    <path d="M20 10 V18" strokeWidth="1" opacity="0.8" />
  </svg>
);

export const VintageCameraIcon = ({ size = 40, className = "" }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect x="6" y="14" width="28" height="18" rx="2" />
    <path d="M14 14 L16 10 H24 L26 14 Z" opacity="0.9" />
    <circle cx="20" cy="23" r="6" />
    <circle cx="20" cy="23" r="4" strokeWidth="1" opacity="0.7" />
    <circle cx="20" cy="23" r="1.5" fill="currentColor" />
    <path d="M10 14 V12M30 14 V12M20 10 V8" strokeWidth="1.5" opacity="0.8" />
    <rect x="8" y="16" width="3" height="3" rx="1" fill="currentColor" strokeWidth="0" opacity="0.6" />
  </svg>
);

export const renderEmojiOrIcon = (val: string, size: number = 24) => {
  if (val === 'HOT_AIR_BALLOON') return <HotAirBalloonIcon size={size} className="text-gold" />;
  return val;
};
